import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { ProblemPicker } from './ProblemPicker';

import { DatePicker } from '@/components/ui/date-picker';
import { type UpsertAssessmentRequest, type TopicAssessment } from '@/lib/api/trainer';
import { RichTextEditor } from '@/components/RichTextEditor';
import { isRichTextEmpty } from '@/components/RichText';

type AssessmentType = 'mcq_quiz' | 'coding_challenge' | 'project_assignment' | 'viva';
type KindKey = 'assignment' | 'project' | 'mcq_quiz' | 'coding_challenge' | 'viva';

// UI-level "kind" maps to (type, assignment_kind). Assignment and Project are both
// the project_assignment type, distinguished by assignment_kind — this is what
// routes them to the Assignments vs Projects category on the Practice hub.
const KINDS: {
  key: KindKey;
  label: string;
  icon: string;
  description: string;
  type: AssessmentType;
  assignmentKind?: 'assignment' | 'project';
}[] = [
  { key: 'assignment', label: 'Assignment', icon: '📋', description: 'Take-home task', type: 'project_assignment', assignmentKind: 'assignment' },
  { key: 'project', label: 'Project', icon: '📦', description: 'Larger capstone', type: 'project_assignment', assignmentKind: 'project' },
  { key: 'mcq_quiz', label: 'MCQ Quiz', icon: '✅', description: 'Auto-graded', type: 'mcq_quiz' },
  { key: 'coding_challenge', label: 'Coding', icon: '💻', description: 'From problem bank', type: 'coding_challenge' },
  // A viva is the one kind with nothing to hand in: the trainer marks the
  // student in the room, from the work item's roster. Authored here like
  // anything else so it can carry a brief, a due date and a rubric.
  { key: 'viva', label: 'Viva', icon: '🎤', description: 'Marked in person', type: 'viva' },
];

function initialKind(a?: TopicAssessment): KindKey {
  if (!a) return 'assignment';
  if (a.type === 'project_assignment') return a.assignment_kind === 'project' ? 'project' : 'assignment';
  return a.type;
}

interface Props {
  initial?: TopicAssessment;
  onSave: (req: UpsertAssessmentRequest) => Promise<void>;
  onCancel: () => void;
}

const inputClass = 'w-full rounded-md border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelClass = 'mb-1.5 block text-xs font-medium text-[#6b7280]';

export function AssessmentForm({ initial, onSave, onCancel }: Props) {
  const [kind, setKind] = useState<KindKey>(initialKind(initial));
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [maxScore, setMaxScore] = useState(initial?.max_score?.toString() ?? '100');
  const [dueDate, setDueDate] = useState(initial?.due_date?.slice(0, 10) ?? '');
  const [difficulty, setDifficulty] = useState(initial?.difficulty ?? '');
  const [askedIn, setAskedIn] = useState((initial?.asked_in ?? []).join(', '));
  const [problemSlug, setProblemSlug] = useState(initial?.problem_slug ?? '');
  const [durationMins, setDurationMins] = useState(initial?.duration_mins?.toString() ?? '');
  const [maxAttempts, setMaxAttempts] = useState(initial?.max_attempts?.toString() ?? '');
  const [questionsPerAttempt, setQuestionsPerAttempt] = useState(
    initial?.questions_per_attempt?.toString() ?? '',
  );
  const [shuffleQuestions, setShuffleQuestions] = useState(initial?.shuffle_questions ?? false);
  const [shuffleOptions, setShuffleOptions] = useState(initial?.shuffle_options ?? false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);

  const selected = KINDS.find((k) => k.key === kind)!;
  const type = selected.type;
  const isProject = type === 'project_assignment';
  const isQuiz = type === 'mcq_quiz';
  // A viva gets the rich brief too — "what to prepare" is the same kind of
  // text as an assignment brief, and the student page renders it under that
  // heading.
  const isBriefed = isProject || type === 'viva';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        title,
        type,
        assignment_kind: selected.assignmentKind,
        description: isBriefed
          ? (isRichTextEmpty(description) ? undefined : description)
          : (description.trim() || undefined),
        max_score: parseInt(maxScore),
        due_date: dueDate ? `${dueDate}T00:00:00Z` : undefined,
        difficulty: difficulty || undefined,
        asked_in: askedIn ? askedIn.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        problem_slug: type === 'coding_challenge' ? problemSlug : undefined,
        // Delivery policy is only authored for quizzes; for the other kinds the
        // fields stay unset so the server writes its own defaults.
        duration_mins: isQuiz && durationMins ? parseInt(durationMins) : undefined,
        max_attempts: isQuiz && maxAttempts ? parseInt(maxAttempts) : undefined,
        questions_per_attempt:
          isQuiz && questionsPerAttempt ? parseInt(questionsPerAttempt) : undefined,
        shuffle_questions: isQuiz ? shuffleQuestions : undefined,
        shuffle_options: isQuiz ? shuffleOptions : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  const isSubmitDisabled =
    busy || !title || parseInt(maxScore) <= 0 || (type === 'coding_challenge' && !problemSlug);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Kind */}
      <div>
        <label className={labelClass}>Type *</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKind(k.key)}
              className={`flex flex-col items-center gap-1 rounded-md border px-2 py-3 text-sm transition-colors ${
                kind === k.key
                  ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                  : 'border-[#ececec] text-[#6b7280] hover:bg-[#fafafa]'
              }`}
            >
              <span className="text-lg">{k.icon}</span>
              <span className="text-xs font-semibold">{k.label}</span>
              <span className="text-[10px] opacity-70">{k.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className={labelClass}>Title *</label>
        <input
          required
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            kind === 'mcq_quiz' ? 'React Hooks Quiz' :
            kind === 'coding_challenge' ? 'Two Sum' :
            kind === 'project' ? 'Personal Portfolio Site' :
            kind === 'viva' ? 'Final Viva — Data Structures' :
            'Build a Todo App'
          }
          className={inputClass}
        />
      </div>

      {/* Coding challenge → problem slug; assignment/project → rich brief; quiz → plain context */}
      {type === 'coding_challenge' ? (
        <div>
          <label className={labelClass}>Problem *</label>
          <ProblemPicker
            value={problemSlug}
            onSelect={(p) => {
              setProblemSlug(p.slug);
              // Auto-fill the title from the chosen problem only when still blank.
              setTitle((t) => (t.trim() ? t : p.title));
            }}
          />
          <p className="mt-1 text-xs text-[#6b7280]">Search the problem bank by title or slug.</p>
        </div>
      ) : isBriefed ? (
        <div>
          <label className={labelClass}>
            {kind === 'project' ? 'Project' : kind === 'viva' ? 'What to prepare' : 'Assignment'} brief
          </label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Objective, requirements, deliverables, submission guidelines…"
          />
          <p className="mt-1 text-xs text-[#6b7280]">
            {kind === 'viva'
              ? 'Shown to the student before the viva. List the topics you will ask about.'
              : 'Shown on the assignment detail page. Use headings and lists for structure.'}
          </p>
        </div>
      ) : (
        <div>
          <label className={labelClass}>Description / instructions</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Optional context for students before starting…"
            className={inputClass}
            style={{ resize: 'vertical' }}
          />
        </div>
      )}

      {/* Score + Due date */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Max score *</label>
          <input
            required
            type="number"
            min="1"
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>{kind === 'viva' ? 'Scheduled for' : 'Due date'}</label>
          <DatePicker value={dueDate} onChange={setDueDate} />
        </div>
      </div>

      {/* MCQ-specific settings */}
      {isQuiz && (
        <div className="space-y-3 rounded-lg border border-[#ececec] bg-[#f3f4f6]/40 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#6b7280]">Quiz settings</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Duration (mins)</label>
              <input
                type="number"
                min="1"
                value={durationMins}
                onChange={(e) => setDurationMins(e.target.value)}
                placeholder="30"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Attempts</label>
              <select
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
                className={inputClass}
              >
                <option value="">Default (1)</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="5">5</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Questions per attempt</label>
              <input
                type="number"
                min="1"
                value={questionsPerAttempt}
                onChange={(e) => setQuestionsPerAttempt(e.target.value)}
                placeholder="All"
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 pt-1">
            <label className="flex items-center gap-2 text-xs text-[#6b7280]">
              <input
                type="checkbox"
                checked={shuffleQuestions}
                onChange={(e) => setShuffleQuestions(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-[#ececec] accent-[#ff5a1f]"
              />
              Shuffle questions
            </label>
            <label className="flex items-center gap-2 text-xs text-[#6b7280]">
              <input
                type="checkbox"
                checked={shuffleOptions}
                onChange={(e) => setShuffleOptions(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-[#ececec] accent-[#ff5a1f]"
              />
              Shuffle options
            </label>
          </div>
        </div>
      )}

      {/* Advanced (difficulty + companies) */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="flex items-center gap-1 text-xs text-[#6b7280] transition-colors hover:text-[#111827]"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          Advanced options
        </button>

        {showAdvanced && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Difficulty</label>
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className={inputClass}>
                <option value="">—</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Asked in (comma-separated)</label>
              <input
                value={askedIn}
                onChange={(e) => setAskedIn(e.target.value)}
                placeholder="Google, Amazon, Meta"
                className={inputClass}
              />
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="rounded-md border border-[#ececec] px-4 py-2 text-sm transition-colors hover:bg-[#fafafa]">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className="rounded-md bg-[#ff5a1f] px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
        >
          {busy ? 'Saving…' : initial ? 'Update assessment' : 'Add assessment'}
        </button>
      </div>
    </form>
  );
}
